import type { Theme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';

export const useDealFormStyles = (
  isForecastingEnabled: boolean,
  forecastContainerRef: React.RefObject<HTMLDivElement | null>
) => {
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const element = forecastContainerRef.current;
    if (!element) return;

    const checkScroll = () => {
      setHasScroll(element.scrollHeight > element.clientHeight);
    };

    checkScroll();

    const resizeObserver = new ResizeObserver(checkScroll);
    const mutationObserver = new MutationObserver(checkScroll);

    resizeObserver.observe(element);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [forecastContainerRef]);

  return useMemo(
    () => ({
      container: {
        overflow: 'hidden',
        contain: 'size',
        height: '100%',
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridTemplateColumns: isForecastingEnabled ? '1fr max(352px, 30%)' : '1fr',
      },
      mainContent: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        borderRight: (theme: Theme) => `1px solid ${theme.palette.divider}`,
        borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
      },
      forecastContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        backgroundColor: (theme: Theme) => theme.palette.background.boxLight as string,
        borderBottom: theme => (hasScroll ? `1px solid ${theme.palette.divider}` : `1px solid transparent`),
      },
      bottomBar: {
        backgroundColor: (theme: Theme) => theme.palette.background.boxLight as string,
        borderRight: (theme: Theme) => `1px solid ${theme.palette.divider}`,
        padding: (theme: Theme) => theme.spacing(1, 4),
        display: 'flex',
        gap: (theme: Theme) => theme.spacing(2),
      },
      forecastButtonContainer: {
        backgroundColor: (theme: Theme) => theme.palette.background.boxLight as string,
        padding: (theme: Theme) => theme.spacing(1, 4),
      },
    }),
    [isForecastingEnabled, hasScroll]
  );
};
